import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CTA_BOTTOM_PAD } from '../../../layouts';
import { useOnboarding } from '../context';
import { previousDiagnosis } from '../record';
import { useResultEngagement } from '../useResultEngagement';
import { ResultReport } from './result';

/**
 * 결과 화면(덱 p10) — 컨텍스트 어댑터. 그리는 건 `ResultReport`가 한다.
 *
 * **회차 레코드가 값의 정본이다.** 녹음이 끝나는 순간(useRecordMachine) 리포트와 레코드가
 * 같이 실리므로 둘은 항상 함께 있다 — 하나만 있으면 그건 버그이고, 화면은 재녹음으로
 * 되돌린다(없는 값을 0으로 그리는 대신).
 *
 * 나가는 길은 플로우의 마지막 `next` — 무엇을 하는지는 페이지가 정한다(`onComplete`:
 * 드래프트를 지우고 홈으로). 결과를 보다 새로고침한 경우는 `OnboardingPage`의
 * `restartFinishedRun`이 같은 일을 한다.
 */

export const ResultView = () => {
  const { t } = useTranslation();
  const { report, record, back, next } = useOnboarding();
  // 기준선은 localStorage를 읽는다 — 렌더마다 다시 읽지 않도록 회차가 바뀔 때만 뽑는다.
  const previous = useMemo(
    () => (record ? previousDiagnosis(record) : null),
    [record],
  );
  // 의견 팝업의 자격은 **첫 진단**의 결과에서만 잰다 — 직전 회차가 없는 회차. 결과가 없는
  // 실패 화면은 본 것이 없으니 재지 않는다.
  const onTabView = useResultEngagement(record !== null && previous === null);

  if (!report || !record) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
        <h2 className="text-xl font-bold">{t('web.result.failed.title')}</h2>
        <p className="text-sm text-muted-foreground">
          {t('web.result.failed.body')}
        </p>
        <button
          type="button"
          onClick={back}
          // 전진·재시도 CTA 는 검은 pill 이다(DESIGN.md — 빨강은 녹음 시작·확정에만).
          className="min-h-[44px] rounded-full bg-black px-6 py-3 font-bold text-white"
        >
          {t('web.result.failed.retry')}
        </button>
      </div>
    );
  }

  return (
    // 이 단계는 셸의 하단 CTA 바를 쓰지 않고 버튼을 본문 끝에 둔다 — 바닥 여백(홈 인디케이터
    // 포함)도 여기서 준다. 없으면 버튼이 화면 끝에 붙어 인디케이터와 겹친다.
    <div
      className="flex flex-1 flex-col gap-6"
      style={{ paddingBottom: CTA_BOTTOM_PAD }}
    >
      <ResultReport record={record} previous={previous} onTabView={onTabView} />
      <button
        type="button"
        onClick={next}
        className="mt-auto flex h-[54px] w-full items-center justify-center rounded-full bg-black font-bold text-white"
      >
        {t('web.result.cta')}
      </button>
    </div>
  );
};
